import { Component, OnInit } from '@angular/core';
import { DisplayOptions, IOptionalSummaryList } from '../../../shared/interfaces/display-options';
import { ReusableFunctions } from '../../../shared/classes/reusable-functions';
import { Router } from '@angular/router';
import { IncentivesService } from '../../services/incentives.service';
import {
    SmallCardData,
    CategoryGroup,
    IChartTitleData,
} from 'app/modules/shared/interfaces/card-data';
import {
    CompensationSmallCardData,
    CompensationByTypeLargeCardData,
    DrawSmallCardsData,
    DrawStatusLargeCardData,
    FundedvsCompensationLargeCardData,
    DistributionOfCompensationPaidLargeCardData,
} from '../../interfaces/incentives-data';
import { NoDataActionType, NoDataSourceType } from '../../../shared/interfaces/no-data';
import { ILargeCardInteriorOptions } from '../../../shared/interfaces/card-data';
import { IncentivesCharts } from '../../../shared/classes/incentives-chart-options';
import { UserAccessService } from '../../../shared/services/user-access.service';
import * as Highcharts from 'highcharts';
import { IPageHeadline } from 'app/modules/shared/classes/page-title-parameters';
import { CurrentUserStateService } from 'app/modules/shared/services/current-user-state.service';

interface ISmallCardData {
    data?: SmallCardData;
    drillDUrlSegment: string;
    index: number;
    title: string;
    subTitle?: string;
    LOonly?: boolean;
}

interface ILargeCardData {
    index: number;
    drillDUrlSegment: string;
    name: string;
    chart: any;
    largeCardInteriorOptions?: ILargeCardInteriorOptions;
}

@Component({
    selector: 'app-incentives',
    templateUrl: './financials-insight-incentives.component.html',
    styleUrls: ['./financials-insight-incentives.component.scss'],
})
export class IncentivesComponent implements OnInit {
    pageTitle: string = 'Incentives';
    displayOptions: DisplayOptions[];
    hFilterJSON: string;
    incentiveCharts = new IncentivesCharts.IncentivesChartOptions(
        this._router,
        this.reusableFunctions
    );
    cardDrilldownUrl: string = '/reporting/financials/';
    colorOptions: string[] = [];
    incentivesSmallCards: ISmallCardData[];
    incentivesLargeCharts: ILargeCardData[];
    mainValueFontSize: number = 34;
    fromDatePeriod: string;
    tenure: number;
    empStatus: number;
    action: NoDataActionType;
    extraParameters: IOptionalSummaryList[];
    navFromUrl: string = 'financials/insights/incentives';
    pageHeadline: IPageHeadline = {
        module: 'financials',
        moduleIcon: 'fa-dollar-sign',
        pageTitle: 'Incentives',
        linkArray: [
            {
                moduleLink: 'financials',
                linkTitle: 'Financials Home',
                showLink: true,
                isHome: true,
            },
            {
                moduleLink: 'financials/insights',
                linkTitle: 'Financials Insights',
                showLink: true,
            },
        ],
    };

    constructor(
        private _incentivesService: IncentivesService,
        private _router: Router,
        private reusableFunctions: ReusableFunctions,
        private _userAccessService: UserAccessService,
        private currentUserService: CurrentUserStateService
    ) {}

    ngOnInit() {
        this.extraParameters = [{ title: 'compareto', value: 'Previous Period' }];
        this.action = NoDataActionType.pageStart;
        this.initializeCards();
        this.hFilterJSON = this.reusableFunctions.getHierarchyFilters();
        this.displayOptions = this.reusableFunctions.getCurrentDisplayOptions(this._router.url);
        this.setDisplayOptionsSelected();
        this.loadData();
    }

    setDisplayOptionsSelected(): void {
        this.fromDatePeriod = this.displayOptions.find(
            (item) => item.optionKey === 'dateRangeRolling'
        ).selectedOptions;
        this.tenure = parseInt(
            this.displayOptions.find((item) => item.optionKey === 'tenure').selectedOptions
        );
        this.empStatus = parseInt(
            this.displayOptions.find((item) => item.optionKey === 'emp-status').selectedOptions
        );
    }

    initializeCards(): void {
        this.incentivesSmallCards = [
            {
                drillDUrlSegment: 'compAmount',
                index: 1,
                title: 'COMPENSATION',
                subTitle: 'avg paid per loan',
                LOonly: false,
            },
            {
                drillDUrlSegment: 'comppBps',
                index: 2,
                title: 'COMPENSATION',
                subTitle: 'avg BPS paid per loan',
                LOonly: false,
            },
            {
                drillDUrlSegment: 'loCommission',
                index: 3,
                title: 'LO Compensation',
                subTitle: 'avg BPS paid per loan',
                LOonly: true,
            },
            {
                drillDUrlSegment: 'unearnedGur',
                index: 4,
                title: 'UNEARNED GUARANTEES',
            },
            {
                drillDUrlSegment: 'drawBalance',
                index: 5,
                title: 'DRAW BALANCE',
            },
            {
                drillDUrlSegment: 'withDrawBalance',
                index: 6,
                title: '# EMPS WITH DRAW BALANCE',
            },
        ];

        this.incentivesLargeCharts = [
            {
                drillDUrlSegment: 'loanCompType',
                index: 1,
                chart: null,
                name: 'Loan Compensation by Type',
            },
            {
                drillDUrlSegment: 'fundedVsComp',
                index: 2,
                chart: null,
                name: 'Funded vs Compensation',
            },
            {
                drillDUrlSegment: 'compPaidDistrib',
                index: 3,
                chart: null,
                name: 'Distribution of LO Compensation',
            },
            {
                drillDUrlSegment: 'drawStatus',
                index: 4,
                chart: null,
                name: 'Draw Status',
            },
        ];
    }

    getEmptySmallCardData(
        cradInf: ISmallCardData,
        hasData = false,
        isDataReady = false,
        action = null
    ): SmallCardData {
        return {
            title: cradInf.title,
            mainValue: '',
            unit: '',
            faIcon: '',
            faIconColor: '',
            comparisonValue: '',
            urlSegment: cradInf.drillDUrlSegment,
            routerLink: this.cardDrilldownUrl,
            svgData: '',
            isDataReady: isDataReady,
            hasData: hasData,
            searchTerm: '',
            action: action ? action : this.action,
            noDataSource: NoDataSourceType.verySmallDataCard,
        };
    }

    getEmptyLargeCardData() {
        this.incentivesLargeCharts.forEach((cardInfo) => {
            let chartTitleData: IChartTitleData = {
                showChartCustomTitle: true,
                chartTile: cardInfo.name,
                chartLabelColor: '',
                chartComparisonValue: '',
                chartMainValue: null,
                chartSecondaryValue: '',
                isMainValueUnitVisible: false,
                showMainValue: false,
                chartSubItemValue: '',
                isChartSubValueVisible: false,
                svgIconString: '',
            };

            cardInfo.largeCardInteriorOptions = {
                action: this.action,
                componentTitle: cardInfo.name,
                searchTerm: '',
                hasData: false,
                isDataReady: false,
                showTitle: true,
                accessToUrl: this.cardDrilldownUrl + cardInfo.drillDUrlSegment,
                uniqueId: this.reusableFunctions.getUniqueId(
                    this.cardDrilldownUrl,
                    cardInfo.drillDUrlSegment
                ),
                chartTitleData: Object.assign({}, chartTitleData),
            };
        });
    }

    loadData(): void {
        this.loadSmallCards();
        this.loadLargeCards();
    }

    loadLargeCards(): void {
        this.getEmptyLargeCardData();
        this._incentivesService
            .getCompensationByTypeLargeCard(
                this.fromDatePeriod,
                this.tenure,
                this.empStatus,
                this.hFilterJSON
            )
            .subscribe(
                (data) => {
                    this.setCompensationByTypeChart(
                        data,
                        this.incentivesLargeCharts[0],
                        data.length > 0
                    );
                },
                () => {
                    this.setCompensationByTypeChart([], this.incentivesLargeCharts[0], false);
                }
            );

        this._incentivesService
            .getFundedvsCompensationLargeCardData(
                this.fromDatePeriod,
                this.tenure,
                this.empStatus,
                this.hFilterJSON
            )
            .subscribe(
                (data) => {
                    this.setFundedvsCompensationChartData(
                        data,
                        this.incentivesLargeCharts[1],
                        data.length > 0
                    );
                },
                () => {
                    this.setFundedvsCompensationChartData([], this.incentivesLargeCharts[1], false);
                }
            );

        this._incentivesService
            .getDistributionOfCompensationPaidLargeCardData(
                this.fromDatePeriod,
                this.tenure,
                this.empStatus,
                this.hFilterJSON
            )
            .subscribe(
                (data) => {
                    this.setDistributionOfCompensationPaidChartData(
                        data,
                        this.incentivesLargeCharts[2],
                        data.length > 0
                    );
                },
                () => {
                    this.setDistributionOfCompensationPaidChartData(
                        [],
                        this.incentivesLargeCharts[2],
                        false
                    );
                }
            );

        this._incentivesService
            .getDrawStatusLargeCardData(
                this.fromDatePeriod,
                this.tenure,
                this.empStatus,
                this.hFilterJSON
            )
            .subscribe(
                (data) => {
                    this.setDrawStatusChartData(
                        data,
                        this.incentivesLargeCharts[3],
                        data.length > 0
                    );
                },
                () => {
                    this.setDrawStatusChartData([], this.incentivesLargeCharts[3], false);
                }
            );
    }

    loadSmallCards(): void {
        this.incentivesSmallCards.forEach((smallCard) => {
            smallCard.data = this.getEmptySmallCardData(smallCard);

            if (smallCard.index === 1 || smallCard.index === 3) {
                this._incentivesService
                    .getCompensationSmallCardData(
                        this.fromDatePeriod,
                        this.tenure,
                        this.empStatus,
                        smallCard.LOonly ? smallCard.LOonly : false,
                        this.hFilterJSON
                    )
                    .subscribe(
                        (data) => {
                            if (smallCard.index === 1) {
                                this.setCompensationSmallCardsData(
                                    data,
                                    smallCard.index + 1,
                                    true,
                                    data.length > 0
                                );
                            }

                            this.setCompensationSmallCardsData(
                                data,
                                smallCard.index,
                                true,
                                data.length > 0
                            );
                        },
                        (error) => {
                            if (smallCard.index === 1) {
                                this.setCompensationSmallCardsData(
                                    [],
                                    smallCard.index + 1,
                                    true,
                                    false,
                                    NoDataActionType.error
                                );
                            }

                            this.setCompensationSmallCardsData(
                                [],
                                smallCard.index,
                                true,
                                false,
                                NoDataActionType.error
                            );
                        }
                    );
            } else if (smallCard.index === 4) {
                this._incentivesService
                    .getDrawSmallCardsData(
                        this.fromDatePeriod,
                        this.tenure,
                        this.empStatus,
                        smallCard.LOonly ? smallCard.LOonly : false,
                        this.hFilterJSON
                    )
                    .subscribe(
                        (data) => {
                            this.setDrawSmallCardsData(data, true, data.length > 0);
                        },
                        (error) => {
                            this.setDrawSmallCardsData([], true, false, NoDataActionType.error);
                        }
                    );
            }
        });
    }

    setFundedvsCompensationChartData(
        inData: FundedvsCompensationLargeCardData[],
        cardInfo: ILargeCardData,
        hasData: boolean
    ): void {
        let data = inData.map((obj) => ({ ...obj }));
        let seriesData = this.getSeriesDataForFundedvsCompensationChart(data);

        let categoryGroup: CategoryGroup[] = this.reusableFunctions.getCategories(
            data,
            ['year', 'monthNumber'],
            this.fromDatePeriod
        )[0];
        let chartOptions = this.incentiveCharts.loadChartOptions(
            'column',
            seriesData,
            null,
            '',
            this.getYaxisForFundedvsCompensationChart(),
            [this.cardDrilldownUrl, cardInfo.drillDUrlSegment],
            categoryGroup,
            this.checkAccess(this.cardDrilldownUrl + cardInfo.drillDUrlSegment),
            true,
            this.navFromUrl
        );
        this.updateLargeCard(cardInfo, chartOptions, hasData);
    }

    setDistributionOfCompensationPaidChartData(
        data: DistributionOfCompensationPaidLargeCardData[],
        cardInfo: ILargeCardData,
        hasData: boolean
    ): void {
        let categoriesAndSeriesData =
            this.getSeriesDataForDistributionOfCompensationPaidChart(data);
        let seriesData = [
            {
                name: 'Distribution',
                data: categoriesAndSeriesData,
                color: '#0066FF',
                fillColor: '#cce0ff',
            },
        ];

        let categoryGroup: CategoryGroup[] = this.reusableFunctions.getCategories(
            data,
            ['year', 'monthNumber'],
            this.fromDatePeriod
        )[0];

        let chartOptions = this.incentiveCharts.loadChartOptions(
            'boxplot',
            null,
            seriesData,
            '',
            this.getYaxisForDistributionOfCompensationPaidChart(),
            [this.cardDrilldownUrl, cardInfo.drillDUrlSegment],
            categoryGroup,
            this.checkAccess(this.cardDrilldownUrl + cardInfo.drillDUrlSegment),
            false,
            this.navFromUrl
        );
        this.updateLargeCard(cardInfo, chartOptions, hasData);
    }

    getYaxisForDistributionOfCompensationPaidChart(): Highcharts.YAxisOptions[] {
        return [
            {
                title: {
                    text: 'BPS',
                    style: {
                        fontSize: '10px',
                        color: '#666666',
                    },
                },
                labels: {
                    enabled: true,
                    style: {
                        fontSize: '10px',
                        color: '#666666',
                    },
                },
                min: 0,
                lineWidth: 1,
            },
        ];
    }

    getYaxisForFundedvsCompensationChart(): Highcharts.YAxisOptions[] {
        return [
            {
                title: {
                    text: 'Volumne ($)',
                    style: {
                        fontSize: '10px',
                        color: '#666666',
                    },
                },
                labels: {
                    enabled: true,
                    style: {
                        fontSize: '10px',
                        color: '#666666',
                    },
                },
                lineWidth: 1,
            },
            {
                title: {
                    text: 'BPS',
                    style: {
                        fontSize: '10px',
                        color: '#666666',
                    },
                },
                opposite: true,
                labels: {
                    enabled: true,
                    style: {
                        fontSize: '10px',
                        color: '#666666',
                    },
                },
                lineWidth: 1,
            },
        ];
    }

    getSeriesDataForDistributionOfCompensationPaidChart(
        data: DistributionOfCompensationPaidLargeCardData[]
    ) {
        let formattedData: IncentivesCharts.BoxplotData[] = [];
        data.forEach((record) => {
            let monthNumToShortName = this.reusableFunctions.monthNumToShortName(
                record.monthNumber
            );
            record['datePeriodDescription'] = monthNumToShortName;
            formattedData.push({
                name: monthNumToShortName,
                description: record.year.toString() + '-' + record.monthNumber.toString(),
                low: record.minimum,
                high: record.maximum,
                median: record.median,
                q1: record.percentile25,
                q3: record.percentile75,
            });
        });
        return formattedData;
    }

    getSeriesDataForFundedvsCompensationChart(
        data: FundedvsCompensationLargeCardData[]
    ): IncentivesCharts.ChartSeries[] {
        let formattedData: IncentivesCharts.ChartSeries[] = [
            {
                name: 'Funded',
                type: 'column',
                color: '#41D6C3',
                data: [],
                yAxis: 0,
            },
            {
                name: 'LO Comm',
                type: 'spline',
                color: '#B870FF',
                data: [],
                yAxis: 1,
            },
            {
                name: 'Total Comp',
                type: 'spline',
                color: '#0066FF',
                data: [],
                yAxis: 1,
            },
        ];
        data.forEach((record) => {
            let monthShortName = this.reusableFunctions.monthNumToShortName(record.monthNumber);
            record['datePeriodDescription'] = monthShortName;
            if (record.period === 'This Period') {
                formattedData[0].data.push({
                    y: Math.round(record.volume),
                    name: monthShortName,
                    description: record.year.toString() + '-' + record.monthNumber.toString(),
                    custom: {
                        dataType: 'money',
                    },
                });

                formattedData[1].data.push({
                    y: Math.round(record.loComp),
                    name: monthShortName,
                    description: record.year.toString() + '-' + record.monthNumber.toString(),
                    custom: {
                        dataType: 'BPS',
                    },
                });

                formattedData[2].data.push({
                    y: record.loComp + record.otherComp,
                    name: monthShortName,
                    description: record.year.toString() + '-' + record.monthNumber.toString(),
                    custom: {
                        dataType: 'BPS',
                    },
                });
            }
        });
        return formattedData;
    }

    setDrawStatusChartData(
        data: DrawStatusLargeCardData[],
        cardInfo: ILargeCardData,
        hasData: boolean
    ): void {
        let months: string[] = [];
        let recaptures: IncentivesCharts.AreaChartDatapoint[] = [];
        let drawBalances: IncentivesCharts.AreaChartDatapoint[] = [];
        let waivers: IncentivesCharts.AreaChartDatapoint[] = [];
        data.forEach((record) => {
            let monthShortName = this.reusableFunctions.monthNumToShortName(record.monthNumber);
            record['datePeriodDescription'] = monthShortName;
            switch (record.category) {
                case 'Recaptured':
                    recaptures.push({
                        y: record.drawAmt,
                        name: monthShortName,
                        description: record.year.toString() + '-' + record.monthNumber.toString(),
                        custom: { selectedPeriod: record.monthNumber.toString() },
                    });
                    break;
                case 'Draw Balance':
                    drawBalances.push({
                        y: record.drawAmt,
                        name: monthShortName,
                        description: record.year.toString() + '-' + record.monthNumber.toString(),
                        custom: { selectedPeriod: record.monthNumber.toString() },
                    });
                    break;
                case 'Waived':
                    waivers.push({
                        y: record.drawAmt,
                        name: monthShortName,
                        description: record.year.toString() + '-' + record.monthNumber.toString(),
                        custom: { selectedPeriod: record.monthNumber.toString() },
                    });
                    break;
                default:
                    throw new Error(
                        'Invalid category in setDrawStatusChartData() in incentives.component.ts: ' +
                            record.category
                    );
            }
        });
        let series: IncentivesCharts.AreaChartSeries[] = [
            {
                name: 'Recaptured',
                data: recaptures,
                color: '#6893B9',
            },
            {
                name: 'Draw Balance',
                data: drawBalances,
                color: '#FF9966',
            },
            {
                name: 'Waived',
                data: waivers,
                color: '#9FA278',
            },
        ];

        let categoryGroup: CategoryGroup[] = this.reusableFunctions.getCategories(
            data,
            ['year', 'monthNumber'],
            this.fromDatePeriod
        )[0];
        let chartOptions = this.incentiveCharts.createNewAreaChart(
            //cardInfo.name,
            'Dollars ($)',
            series,
            0,
            [this.cardDrilldownUrl, cardInfo.drillDUrlSegment],
            categoryGroup,
            this.checkAccess(this.cardDrilldownUrl + cardInfo.drillDUrlSegment),
            false,
            this.navFromUrl
        );
        this.updateLargeCard(cardInfo, chartOptions, hasData);
    }

    setCompensationByTypeChart(
        inData: CompensationByTypeLargeCardData[],
        cardInfo: ILargeCardData,
        hasData: boolean
    ): any {
        let data = inData.map((obj) => ({ ...obj }));
        let commissions: IncentivesCharts.AreaChartDatapoint[] = [];
        let overrides: IncentivesCharts.AreaChartDatapoint[] = [];
        let bonuses: IncentivesCharts.AreaChartDatapoint[] = [];
        data.forEach((record, index) => {
            let monthShortName = this.reusableFunctions.monthNumToShortName(record.monthNumber);
            record['datePeriodDescription'] = monthShortName;
            if (record.period === 'This Period') {
                switch (record.category) {
                    case 'Commission':
                        commissions.push({
                            y: record.bps,
                            name: monthShortName,
                            description:
                                record.year.toString() + '-' + record.monthNumber.toString(),
                        });
                        break;
                    case 'Override':
                        overrides.push({
                            y: record.bps,
                            name: monthShortName,
                            description:
                                record.year.toString() + '-' + record.monthNumber.toString(),
                        });
                        break;
                    case 'Bonus':
                        bonuses.push({
                            y: record.bps,
                            name: monthShortName,
                            description:
                                record.year.toString() + '-' + record.monthNumber.toString(),
                        });
                        break;
                    default:
                        throw new Error(
                            'Invalid category in setCompensationByTypeChart() in incentives.component.ts: ' +
                                record.category
                        );
                }
            }
        });
        let series: IncentivesCharts.AreaChartSeries[] = [
            {
                name: 'Commission',
                data: commissions,
                color: '#B870FF',
            },
            {
                name: 'Override',
                data: overrides,
                color: '#FFCC33',
            },
            {
                name: 'Bonus',
                data: bonuses,
                color: '#57D785',
            },
        ];

        let categoryGroup: CategoryGroup[] = this.reusableFunctions.getCategories(
            data,
            ['year', 'monthNumber'],
            this.fromDatePeriod
        )[0];

        let chartOptions = this.incentiveCharts.createNewAreaChart(
            //cardInfo.name,
            'BPS',
            series,
            3,
            [this.cardDrilldownUrl, cardInfo.drillDUrlSegment],
            categoryGroup,
            this.checkAccess(this.cardDrilldownUrl + cardInfo.drillDUrlSegment),
            true,
            this.navFromUrl
        );
        cardInfo.largeCardInteriorOptions.accessToUrl += '_all';
        this.updateLargeCard(cardInfo, chartOptions, hasData);
    }

    updateLargeCard(cardInfo: ILargeCardData, chartOptions: Highcharts.Options, hasData: boolean) {
        if (cardInfo) {
            cardInfo.largeCardInteriorOptions.chart = new Highcharts.Chart(
                cardInfo.largeCardInteriorOptions.uniqueId,
                chartOptions
            );
            cardInfo.largeCardInteriorOptions.hasData = hasData;
            cardInfo.largeCardInteriorOptions.isDataReady = true;
            cardInfo.largeCardInteriorOptions = Object.assign(
                {},
                cardInfo.largeCardInteriorOptions
            );
        }
    }

    setCompensationSmallCardsData(
        data: CompensationSmallCardData[],
        cardIndex: number,
        isDataReady: boolean,
        hasData: boolean,
        action: NoDataActionType = null
    ): void {
        let thisPeriod = data.find((record) => record.periodGroup === 'This Period');
        let comparePeriod = data.find((record) => record.periodGroup === 'Compare Period');
        let smallCard = this.incentivesSmallCards.find((card) => card.index === cardIndex);

        if (thisPeriod && comparePeriod) {
            let currentValue = cardIndex === 1 ? thisPeriod.avgIncentive : thisPeriod.avgBPS;
            let previousValue = cardIndex === 1 ? comparePeriod.avgIncentive : comparePeriod.avgBPS;
            let comparisonInfo = this.reusableFunctions.calculateComparisonPercentage(
                currentValue,
                previousValue
            );
            let caret = this.reusableFunctions.getCaret(
                comparisonInfo.percentage,
                true,
                null,
                comparisonInfo.isComparable
            );
            let subTitle = this.incentivesSmallCards.find(
                (card) => card.index === cardIndex
            ).subTitle;

            let cardData: SmallCardData = {
                title: smallCard.title,
                subTitle: subTitle,
                mainValue:
                    cardIndex === 1
                        ? this.reusableFunctions.formatMoney(currentValue)
                        : currentValue.toFixed(3),
                unit: '',
                faIcon: caret.ng_caret_icon,
                faIconColor: caret.color,
                comparisonValue: caret.comparison,
                urlSegment: smallCard.drillDUrlSegment,
                routerLink: this.cardDrilldownUrl,
                svgData: caret.svgData,
                isDataReady: isDataReady,
                hasData: hasData,
                searchTerm: '',
            };
            smallCard.data = Object.assign({}, cardData);
        } else {
            smallCard.data = Object.assign(
                {},
                this.getEmptySmallCardData(smallCard, false, true, action)
            );
        }
    }

    setDrawSmallCardsData(
        data: DrawSmallCardsData[],
        isDataReady: boolean,
        hasData: boolean,
        action: NoDataActionType = null
    ): any {
        let thisPeriod = data.find((record) => record.period === 'This Period');
        let comparePeriod = data.find((record) => record.period === 'Compare Period');
        let unEarnedGur = this.incentivesSmallCards.find((card) => card.index === 4);
        let drawBalance = this.incentivesSmallCards.find((smallCard) => smallCard.index === 5);
        let empWithBalance = this.incentivesSmallCards.find((card) => card.index === 6);

        if (thisPeriod && comparePeriod) {
            let currentUnearnedGaurantee = thisPeriod.unearnedGuar;
            let previousUnearnedGaurantee = comparePeriod.unearnedGuar;
            let currentEmpsWithBalance = thisPeriod.nbr;
            let previousEmpsWithBalance = comparePeriod.nbr;
            let currentDrawBalance = thisPeriod.drawBalance;
            let previousDrawBalance = comparePeriod.drawBalance;

            let comparisonPercentageUnearnedGuarantee =
                this.reusableFunctions.calculateComparisonPercentage(
                    currentUnearnedGaurantee,
                    previousUnearnedGaurantee
                );
            let comparisonPercentageDrawBalance =
                this.reusableFunctions.calculateComparisonPercentage(
                    currentDrawBalance,
                    previousDrawBalance
                );
            let comparisonPercentageEmpsWithBalance =
                this.reusableFunctions.calculateComparisonPercentage(
                    currentEmpsWithBalance,
                    previousEmpsWithBalance
                );

            let caretUnearnedGuarantee = this.reusableFunctions.getCaret(
                comparisonPercentageUnearnedGuarantee.percentage,
                true,
                null,
                comparisonPercentageUnearnedGuarantee.isComparable
            );
            let caretDrawBalance = this.reusableFunctions.getCaret(
                comparisonPercentageDrawBalance.percentage,
                true,
                null,
                comparisonPercentageDrawBalance.isComparable
            );
            let caretEmpsWithBalance = this.reusableFunctions.getCaret(
                comparisonPercentageEmpsWithBalance.percentage,
                true,
                null,
                comparisonPercentageEmpsWithBalance.isComparable
            );

            let cardDataUnearnedGuarantee: SmallCardData = this.setSmallCardData(
                unEarnedGur.title,
                currentUnearnedGaurantee.toFixed(2) + '%',
                '',
                caretUnearnedGuarantee,
                unEarnedGur.drillDUrlSegment,
                isDataReady,
                hasData
            );
            unEarnedGur.data = Object.assign({}, cardDataUnearnedGuarantee);

            let cardDataDrawBalance: SmallCardData = this.setSmallCardData(
                drawBalance.title,
                this.reusableFunctions.formatMoney(currentDrawBalance),
                '',
                caretDrawBalance,
                drawBalance.drillDUrlSegment,
                isDataReady,
                hasData
            );
            drawBalance.data = Object.assign({}, cardDataDrawBalance);

            let cardDataEmpsWithBalance: SmallCardData = this.setSmallCardData(
                empWithBalance.title,
                currentEmpsWithBalance.toString(),
                '',
                caretEmpsWithBalance,
                empWithBalance.drillDUrlSegment,
                isDataReady,
                hasData
            );
            empWithBalance.data = Object.assign({}, cardDataEmpsWithBalance);
        } else {
            unEarnedGur.data = Object.assign(
                {},
                this.getEmptySmallCardData(unEarnedGur, false, true, action)
            );
            drawBalance.data = Object.assign(
                {},
                this.getEmptySmallCardData(drawBalance, false, true, action)
            );
            empWithBalance.data = Object.assign(
                {},
                this.getEmptySmallCardData(empWithBalance, false, true, action)
            );
        }
    }

    setSmallCardData(
        title: string,
        mainValue: string,
        units: string,
        caret: any,
        urlSegment: string,
        isDataReady: boolean,
        hasdata: boolean
    ): SmallCardData {
        return {
            title: title,
            mainValue: mainValue,
            unit: units,
            faIcon: caret.ng_caret_icon,
            faIconColor: caret.color,
            comparisonValue: caret.comparison,
            urlSegment: urlSegment,
            routerLink: this.cardDrilldownUrl,
            svgData: caret.svgData,
            isDataReady: isDataReady,
            hasData: hasdata,
            searchTerm: '',
        };
    }

    checkAccess(url: string) {
        return this._userAccessService.hasAccessToPage(url);
    }

    applyNewFilters(event: string) {
        this.action = NoDataActionType.filter;
        this.hFilterJSON = event;
        this.initializeCards();
        this.loadData();
    }

    applyDisplayOptions(event: DisplayOptions[]) {
        this.action = NoDataActionType.filter;
        this.displayOptions = event;
        this.setDisplayOptionsSelected();
        this.initializeCards();
        this.loadData();
    }
}
